import { format, isPast } from "date-fns";
import config from "@config";

const daysPerYear = 365;
const avgDaysPerMonth = 30;
const secondsInMinute = 60;
const secondsInHour = 3600;
const secondsInDay = 86400;
const secondsInWeek = 604800;
const secondsInMonth = 2592000;
const secondsInYear = 31536000;

export const getWeekDay = (date: Date) => {
  return ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][date.getDay()];
};

export const formatDate = value =>
  (parsed => (!value || parsed === "Invalid Date" ? value : parsed))(
    format(value, config.dateFormat),
  );

export const formatMonthYear = value =>
  (parsed => (!value || parsed === "Invalid Date" ? value : parsed))(format(value, "MM/YYYY"));

export const formatFriendlyMonthYear = value =>
  (parsed => (!value || parsed === "Invalid Date" ? value : parsed))(format(value, "MMM-YY"));

export const formatDateTime = value =>
  (parsed => (!value || parsed === "Invalid Date" ? value : parsed))(
    format(value, "DD/MM/YYYY HH:mm"),
  );

export const formatDateTimeWithSeconds = value =>
  (parsed => (!value || parsed === "Invalid Date" ? value : parsed))(
    format(value, "DD/MM/YYYY HH:mm:ss"),
  );

export const formatDateToInputFormat = value =>
  (parsed => (!value || parsed === "Invalid Date" ? value : parsed))(format(value, "YYYY-MM-DD"));

export const formatDateTimeToInputFormat = value =>
  (parsed => (!value || parsed === "Invalid Date" ? value : parsed))(
    format(value, "YYYY-MM-DD H:i:s"),
  );

export const transformHoursInSeconds = (hours: number) => {
  return hours * secondsInHour;
};

export const transformHoursAndMinutesToObject = (time: number) => {
  const hoursAndMinsArr = `${time}`.split(".");
  const hours = Number(hoursAndMinsArr[0]);
  let minutes = 0;
  if (hoursAndMinsArr.length > 1) {
    minutes = Number(hoursAndMinsArr[1]);
  }
  return {
    hours,
    minutes,
  };
};

export const transformTimeToHoursAndMinutes = time => {
  const { hours, minutes } = transformHoursAndMinutesToObject(time);
  if (hours === 24) {
    return `24h 00m`;
  }
  return `${transformHoursToTimeString(hours * 60 + minutes).replace(":", `h `)}m`;
};

export const transformHoursToTimeString = hours => {
  const label = new Date(0, 0);
  label.setMinutes(hours);
  return label.toTimeString().slice(0, 5);
};

export const transformTimeStringToDecimal = t => {
  var arr = t.split(":");
  var dec = parseInt((arr[1] / 6) * 10, 10);
  return parseFloat(parseInt(arr[0], 10) + "." + (dec < 10 ? "0" : "") + dec);
};

export const transformDaysToSeconds = (days: number) => days * secondsInDay;

export const transformWeeksToSeconds = (weeks: number) => weeks * secondsInWeek;

export const transformMonthsToSeconds = (months: number) => months * secondsInMonth;

export const transformSecondsInMinutes = seconds => (seconds / secondsInMinute).toFixed();

export const transformSecondsInHours = (seconds, noToFixed, rounded) => {
  if (noToFixed) {
    const hours = Math.floor(seconds / (60 * 60));
    const divisorForMinutes = seconds % (60 * 60);
    const minutes = Math.floor(divisorForMinutes / 60);
    return `${hours}.${minutes}`;
  }

  if (Number.isInteger(seconds / secondsInHour)) {
    return seconds / secondsInHour;
  }
  if (rounded) {
    return Math.floor(seconds / secondsInHour);
  }
  return Number.parseFloat(Number.parseFloat(seconds / secondsInHour).toFixed(2));
};

export const transformSecondsInDays = (seconds: number) => {
  if (Number.isInteger(seconds / secondsInDay)) {
    return seconds / secondsInDay;
  }
  return (seconds / secondsInDay).toFixed(1);
};

export const transformSecondsInWeeks = (seconds: number) => {
  if (Number.isInteger(seconds / secondsInWeek)) {
    return seconds / secondsInWeek;
  }
  return (seconds / secondsInWeek).toFixed(1);
};

export const transformSecondsInMonths = (seconds: number) => {
  if (Number.isInteger(seconds / secondsInMonth)) {
    return seconds / secondsInMonth;
  }
  return (seconds / secondsInMonth).toFixed(1);
};

export const transformSecondsInYears = seconds => {
  return (seconds / secondsInYear).toFixed(2);
};

export const formatSecondsAsYearMonths = seconds => {
  const days = Math.floor(seconds / secondsInDay);
  const years = Math.floor(days / daysPerYear);
  const months = Math.floor((days - years * daysPerYear) / avgDaysPerMonth);
  if (years > 0) {
    return `${years} years ${months} months`;
  }
  return `${months} months`;
};

export const transformDaysInSeconds = days => (days * secondsInDay).toFixed();

export const transformMinutesInSeconds = minutes => (minutes * secondsInMinute).toFixed();

export const checkIfDateIsPast = value => {
  if (!value) {
    return false;
  }
  return isPast(value);
};

export const formatStartOfDay = (date: string): string | null => {
  if (!date) {
    return null;
  }

  const next = new Date(date);
  next.setUTCHours(0, 0, 0, 0);
  return next.toISOString();
};

export const addLeadingZero = (number: number) => `0${number}`.slice(-2);

export const getHours = miliseconds => {
  const hours = Math.floor((miliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  return addLeadingZero(hours);
};

export const getMinutes = miliseconds => {
  const minutes = Math.floor((miliseconds % (1000 * 60 * 60)) / (1000 * 60));
  return addLeadingZero(minutes);
};

export const getSeconds = miliseconds => {
  const seconds = Math.floor((miliseconds % (1000 * 60)) / 1000);
  return addLeadingZero(seconds);
};

export const getFormattedHoursMinutesSeconds = milliseconds => {
  return `${getHours(milliseconds)}:${getMinutes(milliseconds)}:${getSeconds(milliseconds)}`;
};

export const timeIntervalString = (hour: string | number) => {
  const currentHour = parseInt(hour, 10);
  let nextHour = currentHour === 23 ? 1 : currentHour + 2;
  nextHour = nextHour === 24 ? "00:00" : `${String(nextHour).padStart(2, "0")}:00`;
  return `${String(currentHour).padStart(2, "0")}:00-${nextHour}`;
};
